<template>
  <div class="calendar-small" :class="{collapsed: collapsed}">
    <div class="calendar-title">
      <div class="calendar-nav" v-on:click="monthMove(-1)">
        <font-awesome-icon class="icon" :icon="['fas', 'chevron-left']"/>
      </div>
      <span>{{ app.strings.common.months[calendar.month] }} {{ calendar.year }}</span>
      <div class="calendar-nav" v-on:click="monthMove(1)">
        <font-awesome-icon class="icon" :icon="['fas', 'chevron-right']"/>
      </div>
    </div>
    <div class="calendar-container">
      <div class="calendar-content">
        <div v-for="(day, i) in currentWeek(calendar.week.start, calendar.week.week, calendar.week.days)" :key="'week-' + i" class="week">
          {{ day.locale('es').format('dddd') | truncate(3, '') }}
        </div>
        <div v-for="(day, i) in startDays()" :key="'day-off-' + i" class="day-off">
          {{ day }}
        </div>
        <div v-for="(day, i) in daysInMonth(calendar.month, calendar.year)" :key="'day-' + i" class="day" :class="{hilight: hilight(i), today: isToday(i)}">
          <div v-on:click="dayClick(day)">
            {{ day }}
          </div>
          <div class="practice-marked" :class="{visible: marked(i) && !hilight(i)}"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'calendar-small-component',
  data () {
    return {
      calendar: {
        week: {
          start: 1,
          week: 0,
          days: 7
        },
        day: 0,
        month: 0,
        year: 0,
        _hilighted: {
          day: 0,
          month: 0,
          year: 0
        }
      }
    }
  },
  computed: {

  },
  props: {
    app: {
      type: Object,
      default: () => {
        return {}
      }
    },
    // Leaves only the title row, with the month and its arrows.
    collapsed: {
      type: Boolean,
      default: () => {
        return false
      }
    },
    hilighted: {
      type: Object,
      default: () => {
        return {
          day: 0,
          month: 0,
          year: 0
        }
      }
    },
    marks: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  mounted () {
    this.normalizeHilight()

    let date = new Date();

    this.calendar.day = new Date().getDate()
    if (this._hilighted.day === 0) {
      this.$emit('onDayClick', this.calendar.day)
    }

    // Open on the chosen date when there is one: coming back to this step, the
    // chosen day would be in a month other than the one showing.
    this.calendar.month = this._hilighted.year ? this._hilighted.month : date.getMonth()
    if (this._hilighted.month === 0 && !this._hilighted.year) {
      this.$emit('onMonthMove', this.calendar.month)
    }

    this.calendar.year = this._hilighted.year || date.getFullYear()
    if (this._hilighted.year === 0) {
      this.$emit('onYearMove', this.calendar.year)
    }

    this.$emit('onMove')
  },
  methods: {
    normalizeHilight () {
      this._hilighted = this.hilighted

      if (this._hilighted.day === undefined || this._hilighted.day === null) {
        this._hilighted.day = 0
      }

      if (this._hilighted.month === undefined || this._hilighted.month === null) {
        this._hilighted.month = 0
      }

      if (this._hilighted.year === undefined || this._hilighted.year === null) {
        this._hilighted.year = 0
      }
    },
    startDays () {
      let lastMonth = this.calendar.month - 1
      let lastYear = this.calendar.year

      if (lastMonth === -1) {
        lastMonth = 11
        lastYear--
      }

      let startDay = this.daysInMonth(lastMonth, lastYear) - (this.firstDayInMonth(this.calendar.month, this.calendar.year) - 1)
      let days = []

      for (let i = startDay; i <= this.daysInMonth(lastMonth, lastYear); i++) {
        days.push(i)
      }

      return days
    },
    dayClick (day) {
      this.calendar.day = day
      this.$emit('onDayClick', this.calendar.day)
    },
    monthMove (increment) {
      if (increment === 1) {
        this.calendar.month++
        if (this.calendar.month > 11) {
          this.calendar.month = 0
          this.calendar.year++
          this.$emit('onYearMove', this.calendar.year)
        }
        this.$emit('onMonthMove', this.calendar.month)
      } else if (increment === -1) {
        this.calendar.month--
        if (this.calendar.month < 0) {
          this.calendar.month = 11
          this.calendar.year--
          this.$emit('onYearMove', this.calendar.year)
        }
        this.$emit('onMonthMove', this.calendar.month)
      }
      this.$emit('onMove')
    },
    // Shows a month chosen from outside, as the arrows would.
    moveTo (month, year) {
      this.calendar.year = year
      this.calendar.month = month
      this.$emit('onYearMove', year)
      this.$emit('onMonthMove', month)
      this.$emit('onMove')
    },
    isToday (day) {
      let today = new Date()
      return day + 1 === today.getDate() &&
        this.calendar.month === today.getMonth() &&
        this.calendar.year === today.getFullYear()
    },
    // Reads the prop, not _hilighted: Vue does not watch a field starting with
    // an underscore, so choosing a day did not repaint it.
    hilight (day) {
      let hilighted = this.hilighted
      if (!hilighted) {
        return false
      }
      return day + 1 === hilighted.day &&
        this.calendar.month === hilighted.month &&
        (!hilighted.year || this.calendar.year === hilighted.year)
    },
    marked (day) {
      if (this.marks === undefined) {
        return false
      }

      for (let i in this.marks) {
        let mark = this.marks[i]

        if (mark === day + 1) {
          return true
        }
      }

      return false
    }
  },
  watch: {
    hilighted () {
      this.normalizeHilight()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.calendar-small {
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid var(--va-color-border, #e8edf3);
  border-radius: 14px;
  background-color: var(--va-color-surface, #fff);
}

.calendar-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px;
  border-bottom: 1px solid var(--va-color-border, #e8edf3);
  color: var(--va-color-text-primary, #1f2a37);
  font-size: 16px;
  font-weight: 600;
}

.collapsed .calendar-title {
  border-bottom-color: transparent;
}

.calendar-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: var(--va-color-primary, #2071b7);
  font-size: 15px;
  cursor: pointer;
}

.calendar-container {
  max-height: 420px;
  overflow: hidden;
  transition: max-height 0.25s ease;
}

.collapsed .calendar-container {
  max-height: 0;
}

.calendar-content {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 2px;
  padding: 6px 4px 8px;
}

.calendar-content > div {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  font-size: 15px;
}

.calendar-content > div.week {
  height: 30px;
  color: var(--va-color-text-muted, #64748b);
  font-size: 12px;
}

.calendar-content > div.day-off {
  color: var(--va-color-text-muted, #64748b);
  opacity: 0.35;
}

.calendar-content > div.day {
  position: relative;
  color: var(--va-color-text-primary, #1f2a37);
}

.calendar-content > div.day > div:first-child {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  border-radius: 50%;
  cursor: pointer;
}

.calendar-content > div.day.today > div:first-child {
  border: 2px solid var(--va-color-primary, #2071b7);
  color: var(--va-color-primary, #2071b7);
  font-weight: 600;
}

.calendar-content > div.day.hilight > div:first-child {
  background-color: var(--va-color-primary, #2071b7);
  color: #fff;
  font-weight: 600;
}

.practice-marked {
  position: absolute;
  bottom: 3px;
  width: 5px;
  height: 5px;
  border-radius: 5px;
  background-color: var(--va-color-primary, #2071b7);
  opacity: 0;
}

.practice-marked.visible {
  opacity: 1;
}

// Short phones: the month must leave room for what goes under it.
@media (max-height: 700px) {
  .calendar-content > div {
    height: 38px;
  }

  .calendar-content > div.week {
    height: 24px;
  }

  .calendar-content > div.day > div:first-child {
    width: 36px;
    height: 36px;
  }

  .practice-marked {
    bottom: 0;
  }
}
</style>
